import { Fragment } from 'react';
import { useSearchParams } from '@framework/next/navigation';
import Tooltip, { type ITooltipGroupedListContent } from '@creditflowresearch/uilib/Tooltip';
import LeadLink from './LeadLink';
import {
  getFilledGroups,
  getLeadKey,
  getLeadsQuery,
  getRoleLabel,
  splitInlineLeads,
} from './helpers';
import type { IDealLeadsGroup, IDealLeadsQuery } from './types';

import linkStyles from '@creditflowresearch/uilib/styles/helpers/link.module.scss';
import styles from './dealLeads.module.scss';

interface IDealLeadsProps {
  /**
   * The whole syndicate of the deal or tranche, one group per role, in display order.
   * The tooltip always lists all of it.
   */
  groups: IDealLeadsGroup[],
  /**
   * Roles whose banks the cell itself shows, for a cell that holds one role (a Bond Screener column,
   * a tranche field). All roles by default.
   */
  inlineRoles?: string[],
  /**
   * How many banks the cell shows before "...".
   */
  limit?: number,
  /**
   * Product and region for the lead links when the page URL has none.
   */
  fallbackQuery?: IDealLeadsQuery,
  target?: string,
  dataTest?: string,
}

/**
 * Bank abbreviations of a deal with one tooltip for the whole cell: hovering any bank or "..." shows every bank,
 * grouped by role, as links the pointer can reach.
 */
const DealLeads = ({
  groups,
  inlineRoles,
  limit,
  fallbackQuery,
  target,
  dataTest = 'dealRowLeads',
}: IDealLeadsProps) => {
  const searchParams = useSearchParams();

  const filledGroups = getFilledGroups(groups);
  const inlineGroups = inlineRoles ? filledGroups.filter(({ role }) => inlineRoles.includes(role)) : filledGroups;
  const { visibleLeads, hasHiddenLeads } = splitInlineLeads(inlineGroups, limit);

  if (!visibleLeads.length) {
    return null;
  }

  const query = getLeadsQuery(searchParams, fallbackQuery);

  /**
   * Built only once the tooltip opens: a screener row holds one of these cells per lead column,
   * and each tooltip lists the whole syndicate.
   */
  const getTooltipContent = (): ITooltipGroupedListContent => ({
    format: 'list',
    dataTest: 'dealLeadsTooltip',
    groups: filledGroups.map(({ role, leads }) => ({
      id: role,
      title: `${ getRoleLabel(role, leads.length) }:`,
      items: leads.map((lead) => ({
        id: getLeadKey(lead),
        text: <LeadLink lead={ lead } query={ query } target={ target }>{ lead.name }</LeadLink>,
      })),
    })),
  });

  return (
    <Tooltip content={ getTooltipContent }>
      <span className={ styles.leads } data-test={ dataTest }>
        { visibleLeads.map((lead, index) => (
          <Fragment key={ getLeadKey(lead) }>
            { index > 0 && ', ' }
            <LeadLink lead={ lead } query={ query } target={ target } className={ linkStyles.link }>
              { lead.abbreviation || lead.name }
            </LeadLink>
          </Fragment>
        )) }
        { hasHiddenLeads && <>, <span>...</span></> }
      </span>
    </Tooltip>
  );
};

export default DealLeads;
